import React from 'react'
import { MalindexBesok } from '../../api/malindex/malindexApi'
import { arKartlaggning } from './malindexCalc'

/**
 * Delade visningshjälpare för Målindex.
 *
 * Låg tidigare inne i OversiktHub. När besöksvyn tillkom behövde den samma datumformat
 * och samma etiketter, och två uppsättningar av "brukaren" respektive "hembesök" hade
 * glidit isär vid första ändringen.
 */

/**
 * Datum, utan klockslag. Standardformatet – använd det här om inte annat krävs.
 *
 * Nästan varje datum i Målindex pekar ut ETT besök som sammanhanget redan har pekat
 * ut: "vägs mot behovsskattningen från …", "besöket som påbörjades …". Där säger
 * klockslaget ingenting, och tio sådana rader gör det svårare att läsa de ställen
 * där tiden faktiskt betyder något.
 */
export const formateraDatum = (iso: string) =>
  new Date(iso).toLocaleString('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })

/**
 * Datum med klockslag. Bara i besökslistan.
 *
 * Där, och bara där, ska två besök gå att skilja åt från varandra – ligger de samma
 * dag är tiden det enda som skiljer dem. Överallt annars: `formateraDatum`.
 */
export const formateraDatumTid = (iso: string) =>
  new Date(iso).toLocaleString('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })

/** Kanalen som substantiv. Besök sparade innan fältet fanns får 'kontakt'. */
export const KANAL_ORD: Record<string, string> = {
  hembesok: 'hembesök',
  telefon: 'telefonsamtal',
}

export const kanalOrd = (kanal: string | null) => (kanal ? KANAL_ORD[kanal] ?? kanal : 'kontakt')

/**
 * Pejlens ord för personen är BRUKARE, inte "den enskilde". Det senare är specens
 * myndighetsspråk och krockar med resten av appen.
 *
 * De två nedersta nycklarna finns kvar för besök som sparades innan alternativen
 * ändrades – utan dem skulle deras svarande visas som en rå nyckel.
 */
export const SVARANDE_ETIKETT: Record<string, string> = {
  enskild: 'brukaren',
  anhorig: 'anhörig',
  enskild_anhorig: 'brukaren och anhörig',
  enskilde: 'brukaren',
  tolk: 'tolk',
  blandat: 'blandat',
}

/** "Uppföljande hembesök" – roll och kanal i samma etikett. */
export const besoksEtikett = (b: MalindexBesok) =>
  `${arKartlaggning(b) ? 'Kartläggande' : 'Uppföljande'} ${kanalOrd(b.kanal)}`

/**
 * Skalans ord när frågan om VIKTIGHET inte står bredvid.
 *
 * I skattningsmatrisen räcker "Mycket", eftersom ledtexten "Hur viktigt är det för
 * dig…" står ovanför. I prioriteringssteget och i besöksvyns tabeller finns ingen
 * ledtext att luta sig mot – raden ska gå att läsa fristående.
 *
 * Nyttosidan använder INTE de här orden. "Mycket viktigt" om hur hemtjänsten hjälpt
 * betyder ingenting; där gäller configens vanliga etiketter.
 */
export const VIKTIGT_ORD: Record<number, string> = {
  0: 'Inte alls viktigt',
  1: 'Lite viktigt',
  2: 'Till viss del viktigt',
  3: 'Ganska viktigt',
  4: 'Mycket viktigt',
}

/**
 * Siffrans ruta – en jämn blå ramp ljus→mörk, ett tydligt steg per värde.
 *
 * Noll är genomskinlig med bara en kant: ett svar som betyder "inte alls" ska inte ha
 * någon färgvikt. Stegen är appens egen blå-skala (`pejlen` i tailwind.config.js) och
 * sätts som hex eftersom de ligger i inline-style – ändras skalan där måste de följa med.
 */
export const SIFFERFARG: Record<number, React.CSSProperties> = {
  0: { background: 'transparent', color: '#8c8c8c', border: '1px solid #e8e8e8' },
  1: { background: '#e6f7ff', color: '#15395b' }, // pejlen-50
  2: { background: '#bae7ff', color: '#15395b' }, // pejlen-100
  3: { background: '#69c0ff', color: '#15395b' }, // pejlen-300
  4: { background: '#1890ff', color: '#fff' }, //    pejlen-500 – appens blå
}

/** Svarets siffra i sin färgade ruta. Samma i prioriteringssteget och i besöksvyn. */
export const Sifferruta: React.FC<{ varde: number }> = ({ varde }) => (
  <span
    aria-hidden
    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-sm text-sm font-semibold"
    style={SIFFERFARG[varde] ?? SIFFERFARG[0]}
  >
    {varde}
  </span>
)

/**
 * Sorteringsvärde för en skattning: ej tillämpligt och obesvarat hamnar sist.
 * Utan detta skulle "ej tillämpligt" – lagrat som 9 – sorteras överst, alltså som
 * viktigast av alla.
 */
export const efterVarde = (v: number | undefined, ejTillampligt: number) =>
  v === undefined ? -2 : v === ejTillampligt ? -1 : v
